import { Injectable, NgZone } from "@angular/core";
import { StateService } from "./state.service";

@Injectable({
  providedIn: "root",
})
export class HandleTaskService {
  constructor(
    private stateService: StateService,
    private ngZone: NgZone,
  ) {}

  streamTaskStatus(
    taskId: string,
    onSuccess: (data: any) => void,
    onFailure: (data: any) => void,
    onError: (err?: any) => void,
  ): AbortController {
    const url = this.stateService.apiList.experiments.taskStream(taskId);
    const token = this.stateService.getToken();
    const controller = new AbortController();

    this.stateService.addCalledApi(url);

    this.ngZone.runOutsideAngular(() =>
      this.readStream(url, token, controller, onSuccess, onFailure, onError),
    );

    return controller;
  }

  private async readStream(
    url: string,
    token: string,
    controller: AbortController,
    onSuccess: (data: any) => void,
    onFailure: (data: any) => void,
    onError: (err?: any) => void,
  ): Promise<void> {
    let response: Response;
    try {
      response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "text/event-stream",
        },
        signal: controller.signal,
      });
    } catch (err: any) {
      if (err?.name !== "AbortError") {
        this.stateService.removeCalledApi(url);
        this.ngZone.run(() => onError(err));
      } else {
        this.stateService.removeCalledApi(url);
      }
      return;
    }

    if (!response.ok || !response.body) {
      controller.abort();
      this.stateService.removeCalledApi(url);
      const body = await response.text().catch(() => "");
      this.ngZone.run(() => onError(body));
      return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() ?? "";

        for (const line of lines) {
          if (!line.startsWith("data:")) continue;
          const jsonStr = line.slice(5).trim();
          if (!jsonStr) continue;

          try {
            const data = JSON.parse(jsonStr);

            if (data.status === "SUCCESS") {
              controller.abort();
              this.stateService.removeCalledApi(url);
              this.ngZone.run(() => onSuccess(data));
              return;
            } else if (data.status === "FAILURE") {
              controller.abort();
              this.stateService.removeCalledApi(url);
              this.ngZone.run(() => onFailure(data));
              return;
            }
          } catch {
            // skip malformed SSE data lines
          }
        }
      }
    } catch (err: any) {
      if (err?.name !== "AbortError") {
        this.stateService.removeCalledApi(url);
        this.ngZone.run(() => onError(err));
      } else {
        this.stateService.removeCalledApi(url);
      }
    }
  }
}
